import React, { useState } from 'react'

import { ReviewQueueKind } from '../../../api/reconciliation-v2-api-client'
import AdjustPaymentModal, { AdjustPaymentTarget } from '../AdjustPaymentModal'
import { useCanWriteReconciliation } from '../useCanWriteReconciliation'
import DiscrepanciesPanel from './DiscrepanciesPanel'
import UnpostedTransactionsPanel from './UnpostedTransactionsPanel'

/**
 * One arm of the Required-action queue: a headed table plus the modal its rows
 * open.
 *
 * "Required action" is no longer a tab — since EMS-10248 the two arms are the
 * top-level "Unallocated payments" and "Discrepancies" tabs, and this component is
 * mounted once per arm with the `kind` that says which. The name survives
 * because the UNION still does: one EPS endpoint (`/review-queue`), one `rq*`
 * URL namespace, and this one shell. See CONTEXT.md.
 *
 * It used to be ONE eight-slot card list shared by both kinds of work item,
 * which disagreed about what three of those slots meant — so no single header
 * row could honestly name them, and the rows read as inconsistent (ADR-0001,
 * spec D1). Each arm is now its own table over its own `kind` query, with its
 * own columns, filter vocabulary, search and pagination.
 *
 * What is left here is only what the two arms share: the write gate and the
 * Adjust Payment modal. Everything this shell used to own on top of that has
 * moved up to the page — the labels and their counts to the tab strip, and
 * `rqKind` deleted outright now that the route's own `tab` names the arm.
 */
const ReviewQueue: React.FC<{ kind: ReviewQueueKind }> = ({ kind }) => {
  const [adjustTarget, setAdjustTarget] = useState<AdjustPaymentTarget | null>(
    null
  )
  // Write gate — mirrors the EPS AbilityGuard(Write, PaymentReconciliation) on
  // the allocate/post endpoints these action buttons open the modal to call.
  const { allowed: canWrite } = useCanWriteReconciliation()

  return (
    <div>
      {kind === 'transaction' ? (
        <UnpostedTransactionsPanel
          canWrite={canWrite}
          onOpenTarget={setAdjustTarget}
        />
      ) : (
        <DiscrepanciesPanel
          canWrite={canWrite}
          onOpenTarget={setAdjustTarget}
        />
      )}

      <AdjustPaymentModal
        open={!!adjustTarget}
        target={adjustTarget}
        onClose={() => setAdjustTarget(null)}
      />
    </div>
  )
}

export default ReviewQueue
